import React, { useState } from 'react'
import ButtonGradient from 'component/Buttons/dashboard-btn'
import Table from 'component/Table/table'
import { formateDate } from 'utils'
import Api from 'utils/api'
import { usePlan } from './plan.context'
import CreatePlan from './create.plan'
import EditIcon from 'icon/editIcon'
import { Tooltip } from 'react-tooltip'
import DeleteIcon from 'icon/delete'
import { useOperator } from 'context/operator'
import PlanForm from './PlanForm'
import { LayoutGrid, CreditCard, ListTree, Link as LinkIcon, AlertTriangle, RefreshCw } from 'lucide-react'
import { toast } from 'react-toastify'

const Headers = [
  { name: 'Order', accessor: 'order' },
  { name: 'Created', accessor: 'created' },
  { name: 'Plan Name', accessor: 'name' },
  { name: 'Currency', accessor: 'currency' },
  { name: 'Fees ($)', accessor: 'fees' },
  { name: 'Free Minutes', accessor: 'freeMinutes' },
  { name: 'Minutes Limit', accessor: 'minutesLimit' },
  { name: 'Actions', accessor: 'actions' },
]

const billingUnitLabel = (billingType) => {
  if (billingType === 'per_hour') return '/hr'
  if (billingType === 'flat_rate') return ' flat'
  return '/min'
}

function Plans() {
  const { plans, setPlans, setOpenModal, setSelectedPlan, setShowConfirmDelete } = usePlan()
  const { operator } = useOperator()
  const [activeTab, setActiveTab] = useState('consumption')
  const [cloningId, setCloningId] = useState(null)

  const needsMigration = (plan) =>
    plan.stripePlanId && plan.stripeAccountSource !== 'super_admin'

  const handleClone = async (plan) => {
    setCloningId(plan._id)
    const { response, error } = await Api('/plans/clone', 'post', {
      payload: { _id: plan._id },
      notify: false,
    })
    setCloningId(null)
    if (response?.success) {
      toast.success(`"${plan.productname}" migrated to super-admin Stripe account`)
      setPlans((prev) => prev.map((p) => (p._id === plan._id ? response.data : p)))
    } else {
      toast.error(error?.message || 'Migration failed — check Stripe configuration')
    }
  }

  // Display order: explicit sortOrder ascending, then lowest price (unlockFee +
  // subscriptionFee) as tiebreaker — mirrors the backend sortPlans() helper.
  const sortByDisplayOrder = (list) =>
    [...list].sort((a, b) => {
      const order = (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0)
      if (order !== 0) return order
      return (
        ((Number(a?.unlockFee) || 0) + (Number(a?.subscriptionFee) || 0)) -
        ((Number(b?.unlockFee) || 0) + (Number(b?.subscriptionFee) || 0))
      )
    })

  const subscriptionPlans = sortByDisplayOrder(plans.filter(p => p.isSubscription))
  const payAsYouGoPlans = sortByDisplayOrder(plans.filter(p => !p.isSubscription))

  const activePlans = activeTab === 'subscription' ? subscriptionPlans : payAsYouGoPlans

  const handleUpdate = (plan) => {
    setSelectedPlan(plan)
    setOpenModal(true)
  }

  const handleDelete = (plan) => {
    setShowConfirmDelete(true)
    setSelectedPlan(plan)
  }

  const Rows = activePlans.map((plan) => {
    const formattedDate = formateDate(plan.createdAt)
    const dateParts = formattedDate.replace(',', '').split(' ')
    const month = dateParts[0] || ''
    const day = dateParts[1] || ''
    const year = dateParts[2] || ''

    return {
      order: (
        <span className='inline-flex items-center justify-center min-w-[28px] h-7 px-2 bg-gray-900 text-white rounded-lg text-xs font-black tabular-nums'>
          {plan?.sortOrder ?? 0}
        </span>
      ),
      created: (
        <div className='flex flex-col'>
          <span className='font-bold text-gray-900'>{month} {day}</span>
          <span className='text-[10px] text-gray-400 font-black uppercase tracking-widest'>{year}</span>
        </div>
      ),
      currency: (
        <span className='px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-black tracking-widest'>
          {plan?.currency || 'USD'}
        </span>
      ),
      fees: (
        <div className='flex items-center gap-2'>
          {plan.isSubscription ? (
            <>
              <span className='text-[#BA141B] font-black'>${plan?.subscriptionFee || 0}</span>
              <span className='text-gray-300 text-[10px] uppercase font-black'>/{plan?.interval || 'mo'}</span>
              <span className='text-gray-200'>|</span>
              <span className='text-blue-600 font-bold text-xs'>${plan?.unitFee || 0}{billingUnitLabel(plan?.billingType)}</span>
            </>
          ) : (
            <>
              {plan?.billingType === 'flat_rate' ? (
                <span className='text-emerald-600 font-black'>${plan?.unlockFee || 0} flat</span>
              ) : (
                <>
                  <span className='text-emerald-600 font-black'>${plan?.unlockFee || 0} unlock</span>
                  <span className='text-gray-300'>+</span>
                  <span className='text-blue-600 font-bold'>${plan?.unitFee || 0}{billingUnitLabel(plan?.billingType)}</span>
                </>
              )}
              {plan?.isPromo && (
                <span className='ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black rounded uppercase tracking-wider'>PROMO</span>
              )}
            </>
          )}
        </div>
      ),
      freeMinutes: (
        <span className='font-mono font-black text-blue-500'>
          {plan?.freeMinutes ? `${plan.freeMinutes}m` : '0m'}
        </span>
      ),
      minutesLimit: (
        <span className='font-mono font-black text-gray-500'>
          {plan?.minutesLimit || '∞'}
        </span>
      ),
      name: (
        <div className='flex items-center gap-3'>
          <div className={`w-2 h-2 rounded-full ${plan.isSubscription ? 'bg-purple-400 shadow-[0_0_10px_rgba(167,139,250,0.5)]' : 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]'}`}></div>
          <div className='font-black text-gray-900 uppercase tracking-tight'>{plan?.productname}</div>
        </div>
      ),
      actions: (
        <div className='flex items-center gap-3 flex-wrap'>
          {needsMigration(plan) && (
            <button
              onClick={() => handleClone(plan)}
              disabled={cloningId === plan._id}
              title='This plan was created with an operator Stripe key. Click to migrate it to the super-admin Stripe account.'
              className='flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-300 text-amber-700 rounded-xl text-[10px] font-black uppercase tracking-wide hover:bg-amber-100 transition-all disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {cloningId === plan._id ? (
                <RefreshCw size={12} className='animate-spin' />
              ) : (
                <AlertTriangle size={12} />
              )}
              {cloningId === plan._id ? 'Migrating…' : 'Switch Account'}
            </button>
          )}

          <button
            onClick={() => handleUpdate(plan)}
            className='p-2 hover:bg-blue-50 text-blue-600 rounded-xl transition-all active:scale-90 shadow-sm'
            title='Edit Schema'
          >
            <EditIcon size={18} />
          </button>

          <button
            onClick={() => handleDelete(plan)}
            className='p-2 hover:bg-red-50 text-red-600 rounded-xl transition-all active:scale-90 shadow-sm'
            title='Retire Plan'
          >
            <DeleteIcon size={18} />
          </button>
        </div>
      ),
    }
  })

  return (
    <div className='space-y-10 max-w-[1600px] mx-auto p-4 lg:p-8'>
      {/* Header Info */}
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-6'>
        <div className='space-y-1'>
          <h1 className='text-5xl font-black text-gray-900 tracking-tighter'>
            REVENUE <span className='text-[#BA141B]'>SCHEMAS</span>
          </h1>
          <p className='text-xs font-black text-gray-400 uppercase tracking-[0.4em]'>System Plan Architecture & Monetization</p>
        </div>

        <div className='flex items-center gap-3'>
          <ButtonGradient
            onClick={() => window.open('/payment/plans?operator=' + operator._id)}
            className='h-12 px-8 font-black rounded-2xl shadow-2xl flex items-center gap-2'
          >
            <LinkIcon className='w-4 h-4' />
            EXT. PLAN VIEW
          </ButtonGradient>
          <ButtonGradient
            onClick={() => setOpenModal(true)}
            className='h-12 px-8 font-black rounded-2xl bg-black text-white hover:bg-[#BA141B] transition-all shadow-2xl shadow-red-100'
          >
            GENERATE PLAN
          </ButtonGradient>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className='flex gap-4 p-1.5 bg-gray-100/50 rounded-[24px] w-fit border border-gray-100'>
        <button
          onClick={() => setActiveTab('consumption')}
          className={`flex items-center gap-3 px-8 py-3 rounded-[18px] text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === 'consumption'
              ? 'bg-white text-gray-900 shadow-xl'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <LayoutGrid className={`w-4 h-4 ${activeTab === 'consumption' ? 'text-emerald-500' : 'text-gray-400'}`} />
          Consumption Logic
        </button>
        <button
          onClick={() => setActiveTab('subscription')}
          className={`flex items-center gap-3 px-8 py-3 rounded-[18px] text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === 'subscription'
              ? 'bg-white text-gray-900 shadow-xl'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <CreditCard className={`w-4 h-4 ${activeTab === 'subscription' ? 'text-purple-500' : 'text-gray-400'}`} />
          Master Subscriptions
        </button>
      </div>

      <div className='grid grid-cols-1 gap-12'>
        <section className='space-y-6'>
          <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <div className={`p-3 rounded-2xl ${activeTab === 'subscription' ? 'bg-purple-900' : 'bg-gray-900'}`}>
                {activeTab === 'subscription' ? <ListTree className='w-6 h-6 text-white' /> : <LayoutGrid className='w-6 h-6 text-white' />}
              </div>
              <div>
                <h2 className='text-xl font-black text-gray-900 tracking-tight uppercase'>
                  {activeTab === 'subscription' ? 'Recurring Revenue Nodes' : 'Granular Consumption Logics'}
                </h2>
                <p className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>
                  {activeTab === 'subscription' ? 'Subscription Protocol Inventory' : 'Pay-Per-Ride Operational Nodes'}
                </p>
              </div>
            </div>
            
            <div className='flex items-center gap-2'>
              <span className='px-4 py-1.5 bg-gray-50 text-gray-500 text-[10px] font-black rounded-xl uppercase border border-gray-100'>
                Total Nodes: {activePlans.length}
              </span>
            </div>
          </div>
          
          {activePlans.some(needsMigration) && (
            <div className='flex items-start gap-4 p-5 bg-amber-50 border-2 border-amber-200 rounded-[24px]'>
              <AlertTriangle className='w-5 h-5 text-amber-500 mt-0.5 shrink-0' />
              <div className='space-y-1'>
                <p className='text-xs font-black text-amber-800 uppercase tracking-wide'>
                  Stripe Account Mismatch Detected
                </p>
                <p className='text-[11px] text-amber-700 leading-relaxed'>
                  {activePlans.filter(needsMigration).length} plan(s) below were created with an operator-level Stripe key and are not compatible with the current super-admin Stripe account.
                  Use the <strong>Switch Account</strong> button on each plan to clone it into the super-admin account before processing payments.
                </p>
              </div>
            </div>
          )}

          <div className='bg-white rounded-[32px] border-2 border-gray-100 shadow-xl overflow-hidden'>
            <Table columns={Headers} rows={Rows} />
            {activePlans.length === 0 && (
              <div className='p-20 text-center space-y-4'>
                <div className='w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-300'>
                  {activeTab === 'subscription' ? <CreditCard size={32} /> : <LayoutGrid size={32} />}
                </div>
                <p className='text-xs font-black text-gray-400 uppercase tracking-widest'>No {activeTab} plans mapped to the system yet</p>
              </div>
            )}
          </div>
        </section>
      </div>

      <CreatePlan />
    </div>
  )
}

export default Plans
